import isArray from 'lodash/isArray';
import PropTypes from 'prop-types';
import React, {useMemo} from 'react';
import {Transition, TransitionGroup} from 'react-transition-group';
import {handleEnterAnimation, handleExitAnimation} from '../../animations/timelines/modal-transitions';
import ERROR_FIELD from '../../constants/error-fields';
import REVIEW_TYPES from '../../constants/review-types';
import {Permission} from '../../helpers/user';
import BasicDialog from '../../scenes/Dialog/components/BasicDialog';
import ResponseErrorDialog from '../../scenes/Dialog/components/ResponseErrorDialog';
import AddReviewCompanyForm from '../../scenes/Form/components/AddReviewCompanyForm';
import AddReviewEmployeeForm from '../../scenes/Form/components/AddReviewEmployeeForm';
import AddReviewInterviewForm from '../../scenes/Form/components/AddReviewInterviewForm';
import './styles.css';

const ReviewFormSwitcher = props => {
  const { firm = null, position = null, user, formType, onChange } = props;

  const permission = useMemo(
    () => ({
      overview: Permission.isAllowedAddOverviewReview(user.permissions),
      interview: Permission.isAllowedAddInterviewReview(user.permissions),
      employee: Permission.isAllowedAddEmployeeReview(user.permissions),
    }),
    [user]
  );

  const handleError = (response, setErrors) => {
    const {
      data: { fields },
    } = response;
    if (fields && typeof setErrors === 'function') {
      const obj = {};
      for (let [key, value] of Object.entries(fields)) {
        const error = isArray(value) ? value[0] : value;
        obj[key] = ERROR_FIELD[error] || `${error}`;
      }
      setErrors(obj);
    } else {
      ResponseErrorDialog({ response });
    }
  };

  const handleSwitchForm = type => {
    onChange(type);
  };

  const handleActiveCompanyForm = () => {
    if (permission.overview) {
      handleSwitchForm(REVIEW_TYPES.COMPANY);
    }
  };

  const handleActiveInterviewForm = () => {
    if (permission.interview) {
      handleSwitchForm(REVIEW_TYPES.INTERVIEW);
    }
  };

  const handleActiveEmployeeForm = () => {
    if (permission.employee) {
      handleSwitchForm(REVIEW_TYPES.EMPLOYEE);
    }
  };

  const handleAfterFormSave = () => {
    BasicDialog({
      bodyRender: 'Thank you for your submission!',
      textSuccessBtn: 'Add Another Review',
    });

    const elements = document.getElementsByClassName('form-review-switcher');
    if (elements.length > 0) {
      elements[0].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start',
      });
    }
  };

  const form = useMemo(() => {
    if (formType === REVIEW_TYPES.COMPANY) {
      return (
        <AddReviewCompanyForm
          params={{ user, firm }}
          actions={{ handleError, proceed: handleAfterFormSave }}
        />
      );
    }

    if (formType === REVIEW_TYPES.INTERVIEW) {
      return (
        <AddReviewInterviewForm
          params={{ user, firm }}
          actions={{ handleError, proceed: handleAfterFormSave }}
        />
      );
    }

    if (formType === REVIEW_TYPES.EMPLOYEE) {
      return (
        <AddReviewEmployeeForm
          params={{ user, firm, position }}
          actions={{ handleError, proceed: handleAfterFormSave }}
        />
      );
    }
  }, [formType, firm, position]);

  return (
    <div>
      <div className="form-review-switcher">
        <h2 className="form-review-switcher__title">Select Review Type</h2>
        <div className="form-review-switcher__control-wrapper">
          <div className="form-review-switcher__control">
            <div
              onClick={handleActiveCompanyForm}
              className={`form-review-switcher__link ${
                formType === REVIEW_TYPES.COMPANY
                  ? ' form-review-switcher__link--selected'
                  : permission.overview
                  ? ''
                  : ' form-review-switcher__link--disabled'
              }`}
            >
              Company Review
            </div>
          </div>
          <div className="form-review-switcher__control">
            <div
              onClick={handleActiveInterviewForm}
              className={`form-review-switcher__link ${
                formType === REVIEW_TYPES.INTERVIEW
                  ? ' form-review-switcher__link--selected'
                  : permission.interview
                  ? ''
                  : ' form-review-switcher__link--disabled'
              }`}
            >
              Interview Review
            </div>
          </div>
          <div className="form-review-switcher__control">
            <div
              onClick={handleActiveEmployeeForm}
              className={`form-review-switcher__link ${
                formType === REVIEW_TYPES.EMPLOYEE
                  ? ' form-review-switcher__link--selected'
                  : permission.employee
                  ? ''
                  : ' form-review-switcher__link--disabled'
              }`}
            >
              Employee Review
            </div>
          </div>
        </div>
      </div>

      <TransitionGroup component={null}>
        <Transition
          key={formType}
          timeout={500}
          mountOnEnter={true}
          unmountOnExit={true}
          onEnter={node => handleEnterAnimation(node)}
          onExit={node => handleExitAnimation(node)}
        >
          <section className="form-review-switcher__section">
            <div className="form-review-switcher__section--content">{form}</div>
          </section>
        </Transition>
      </TransitionGroup>
    </div>
  );
};

ReviewFormSwitcher.propTypes = {
  firm: PropTypes.oneOfType([
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
    PropTypes.string,
  ]),
  position: PropTypes.string,
  formType: PropTypes.string.isRequired,
  user: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default ReviewFormSwitcher;
